import {
  FiCalendar,
  FiClock,
  FiDollarSign,
  FiDroplet,
  FiMapPin,
  FiScissors,
  FiUsers,
} from "react-icons/fi";
import { formatDate, formatTime } from "../../utils/formatDate";
import formatCurrency from "../../utils/formatCurrency";
import {
  JAGUAR_HENNA_DISCLAIMER,
  MOBILE_MODE_DESCRIPTION,
  MOBILE_TRAVEL_EXPENSES_DISCLAIMER,
  SERVICE_MODE,
  STUDIO_MODE_DESCRIPTION,
} from "../../utils/constants";

const DetailRow = ({ icon, label, value }) => {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 text-henna">{icon}</div>
      <div>
        <p className="text-sm font-medium text-[#3b2f2f]">{label}</p>
        <p className="mt-1 text-sm leading-6 text-muted-warm">{value || "—"}</p>
      </div>
    </div>
  );
};

const BookingSummary = ({
  bookingData = {},
  bookingItemsWithDetails = [],
  totals = {
    totalPartySize: 0,
    totalPrice: 0,
    totalDurationMinutes: 0,
    serviceMode: SERVICE_MODE.STUDIO,
  },
  className = "",
  title = "Appointment Summary",
  showDescriptions = true,
}) => {
  const {
    bookingDate,
    startTime,
    endTime,
    serviceMode,
    locationNotes,
    onsiteAddress,
    notes,
  } = bookingData || {};

  const addressText =
    onsiteAddress && serviceMode === SERVICE_MODE.MOBILE
      ? [
          onsiteAddress.line1,
          onsiteAddress.line2,
          onsiteAddress.city,
          onsiteAddress.state,
          onsiteAddress.zip,
        ]
          .filter(Boolean)
          .join(", ")
      : "";

  const modeDescription =
    serviceMode === SERVICE_MODE.MOBILE
      ? MOBILE_MODE_DESCRIPTION
      : serviceMode === SERVICE_MODE.STUDIO
      ? STUDIO_MODE_DESCRIPTION
      : "";

  const hasJaguarService = bookingItemsWithDetails.some(
    (item) => item?.hennaType === "Jaguar"
  );

  return (
    <aside className={`content-card-lg p-6 ${className}`}>
      <h2 className="text-xl font-semibold text-henna">{title}</h2>
      <p className="mt-2 text-sm leading-6 text-muted-warm">
        Review your selected services, overall totals, and appointment details
        before you submit.
      </p>

      <div className="mt-6 space-y-5">
        <DetailRow
          icon={<FiDollarSign size={18} />}
          label="Estimated Total Price"
          value={formatCurrency(totals.totalPrice || 0)}
        />

        <DetailRow
          icon={<FiClock size={18} />}
          label="Estimated Total Duration"
          value={`${totals.totalDurationMinutes || 0} minutes`}
        />

        <DetailRow
          icon={<FiCalendar size={18} />}
          label="Date"
          value={bookingDate ? formatDate(bookingDate) : "Not selected"}
        />

        <DetailRow
          icon={<FiClock size={18} />}
          label="Time"
          value={
            startTime && endTime
              ? `${formatTime(startTime)} - ${formatTime(endTime)}`
              : "Not selected"
          }
        />

        <DetailRow
          icon={<FiUsers size={18} />}
          label="Party Size"
          value={String(totals.totalPartySize || 0)}
        />

        <DetailRow
          icon={<FiMapPin size={18} />}
          label="Appointment Type"
          value={serviceMode || "Not selected"}
        />

        {showDescriptions && modeDescription ? (
          <div className="rounded-2xl bg-soft-cream p-4">
            <p className="text-sm font-semibold text-henna">What This Means</p>
            <p className="mt-2 text-sm leading-6 text-muted-warm">
              {modeDescription}
            </p>
          </div>
        ) : null}

        <div className="rounded-2xl border border-[#ead8c0] bg-[#fffdfa] p-4">
          <p className="text-sm font-semibold text-henna">Booked Services</p>

          {bookingItemsWithDetails.length === 0 ? (
            <p className="mt-3 text-sm text-muted-warm">
              Add at least one service to see the breakdown here.
            </p>
          ) : (
            <div className="mt-4 space-y-4">
              {bookingItemsWithDetails.map((item, index) => (
                <div
                  key={`${item.serviceId || "service"}-${index}`}
                  className="rounded-2xl bg-soft-cream p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 text-henna">
                      <FiScissors size={18} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-[#3b2f2f]">
                        {item?.service?.serviceName || `Service ${index + 1}`}
                      </p>

                      <div className="mt-2 space-y-1 text-sm leading-6 text-muted-warm">
                        <p>
                          <span className="font-medium text-[#3b2f2f]">
                            Henna type:
                          </span>{" "}
                          {item?.hennaType || "Not selected"}
                        </p>
                        <p>
                          <span className="font-medium text-[#3b2f2f]">
                            Adults:
                          </span>{" "}
                          {item?.numOfAdults || 0}
                        </p>
                        <p>
                          <span className="font-medium text-[#3b2f2f]">
                            Children:
                          </span>{" "}
                          {item?.numOfChildren || 0}
                        </p>
                        <p>
                          <span className="font-medium text-[#3b2f2f]">
                            Party size:
                          </span>{" "}
                          {item?.partySize || 0}
                        </p>
                        <p>
                          <span className="font-medium text-[#3b2f2f]">
                            Duration:
                          </span>{" "}
                          {item?.bookingDurationMinutes || 0} minutes
                        </p>
                        <p>
                          <span className="font-medium text-[#3b2f2f]">
                            Price:
                          </span>{" "}
                          {formatCurrency(item?.bookingPrice || 0)}
                        </p>
                      </div>

                      {item?.hennaType === "Jaguar" ? (
                        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
                          <div className="flex items-start gap-2">
                            <FiDroplet className="mt-0.5 text-henna" size={16} />
                            <p className="text-sm leading-6 text-muted-warm">
                              {JAGUAR_HENNA_DISCLAIMER}
                            </p>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {serviceMode === SERVICE_MODE.MOBILE ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-semibold text-henna">Mobile Appointment Notice</p>
            <p className="mt-2 text-sm leading-6 text-muted-warm">
              {MOBILE_TRAVEL_EXPENSES_DISCLAIMER}
            </p>
          </div>
        ) : null}

        {hasJaguarService ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-semibold text-henna">Jaguar Henna Notice</p>
            <p className="mt-2 text-sm leading-6 text-muted-warm">
              {JAGUAR_HENNA_DISCLAIMER}
            </p>
          </div>
        ) : null}

        {serviceMode === SERVICE_MODE.MOBILE && addressText ? (
          <DetailRow
            icon={<FiMapPin size={18} />}
            label="Service Address"
            value={addressText}
          />
        ) : null}

        {serviceMode === SERVICE_MODE.MOBILE && locationNotes ? (
          <DetailRow
            icon={<FiMapPin size={18} />}
            label="Location Notes"
            value={locationNotes}
          />
        ) : null}

        {notes ? (
          <DetailRow
            icon={<FiScissors size={18} />}
            label="Notes"
            value={notes}
          />
        ) : null}
      </div>
    </aside>
  );
};

export default BookingSummary;
